import React from 'react';
import { Box, Container, Typography, Paper, Divider } from '@mui/material';
import { motion } from 'framer-motion';

const TermsOfService = () => {
  return (
    <Box sx={{ py: 8, minHeight: '100vh' }}>
      <Container>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Typography 
            variant="h2" 
            component="h1" 
            gutterBottom 
            align="center"
            sx={{ fontWeight: 'bold', mb: 2 }}
          >
            Terms of Service
          </Typography>
          <Typography 
            variant="body1" 
            align="center" 
            color="text.secondary" 
            sx={{ mb: 6 }}
          >
            Last Updated: January 4, 2026
          </Typography>

          <Paper
            elevation={0}
            sx={{
              p: { xs: 3, md: 5 },
              backgroundColor: 'background.paper',
              borderRadius: 3,
            }}
          >
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', mb: 3 }}>
              1. Agreement to Terms
            </Typography>
            <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
              By accessing or using InnoVate Games' website and mobile applications, you agree to be bound by these Terms 
              of Service. If you disagree with any part of these terms, you may not access our services.
            </Typography>

            <Divider sx={{ my: 4 }} />

            <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', mb: 3 }}>
              2. Use License
            </Typography>
            <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
              We grant you a personal, non-exclusive, non-transferable, limited license to access and use our games and 
              services for your personal, non-commercial use, subject to these Terms of Service.
            </Typography>
            <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
              You agree not to:
            </Typography>
            <Typography variant="body1" component="div" sx={{ lineHeight: 1.8, mb: 2 }}>
              <ul>
                <li>Modify, reverse engineer, or create derivative works based on our games</li>
                <li>Use our games for any illegal or unauthorized purpose</li>
                <li>Attempt to gain unauthorized access to our systems or networks</li>
                <li>Interfere with or disrupt the integrity or performance of our games</li>
                <li>Upload viruses or malicious code</li>
                <li>Collect or harvest any information from our services</li>
              </ul>
            </Typography>

            <Divider sx={{ my: 4 }} />

            <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', mb: 3 }}>
              3. User Accounts
            </Typography>
            <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
              When you create an account with us, you must provide accurate, complete, and current information. You are 
              responsible for maintaining the confidentiality of your account credentials and for all activities that 
              occur under your account.
            </Typography>

            <Divider sx={{ my: 4 }} />

            <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', mb: 3 }}>
              4. Intellectual Property
            </Typography>
            <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
              All content, features, and functionality of our games, including but not limited to graphics, logos, text, 
              images, code, and software, are the exclusive property of InnoVate Games and are protected by international 
              copyright, trademark, and other intellectual property laws.
            </Typography>

            <Divider sx={{ my: 4 }} />

            <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', mb: 3 }}>
              5. User-Generated Content
            </Typography>
            <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
              Some of our games may allow you to submit content such as usernames, profiles, or other materials. By 
              submitting content, you grant us a worldwide, non-exclusive, royalty-free license to use, reproduce, and 
              display that content in connection with our services.
            </Typography>
            <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
              You represent and warrant that:
            </Typography>
            <Typography variant="body1" component="div" sx={{ lineHeight: 1.8, mb: 2 }}>
              <ul>
                <li>You own or have the necessary rights to the content you submit</li>
                <li>Your content does not violate any third-party rights</li>
                <li>Your content does not contain illegal, harmful, or offensive material</li>
              </ul>
            </Typography>

            <Divider sx={{ my: 4 }} />

            <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', mb: 3 }}>
              6. In-App Purchases
            </Typography>
            <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
              Our games may offer in-app purchases for virtual items or premium features. All purchases are final and 
              non-refundable except as required by law. Prices are subject to change without notice.
            </Typography>

            <Divider sx={{ my: 4 }} />

            <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', mb: 3 }}>
              7. Disclaimers
            </Typography>
            <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
              Our games and services are provided "as is" and "as available" without warranties of any kind, either express 
              or implied. We do not warrant that our services will be uninterrupted, secure, or error-free.
            </Typography>

            <Divider sx={{ my: 4 }} />

            <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', mb: 3 }}>
              8. Limitation of Liability
            </Typography>
            <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
              To the maximum extent permitted by law, InnoVate Games shall not be liable for any indirect, incidental, 
              special, consequential, or punitive damages, or any loss of profits or revenues, whether incurred directly 
              or indirectly, or any loss of data, use, goodwill, or other intangible losses.
            </Typography>

            <Divider sx={{ my: 4 }} />

            <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', mb: 3 }}>
              9. Termination
            </Typography>
            <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
              We may terminate or suspend your account and access to our services immediately, without prior notice or 
              liability, for any reason, including if you breach these Terms of Service.
            </Typography>

            <Divider sx={{ my: 4 }} />

            <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', mb: 3 }}>
              10. Governing Law
            </Typography>
            <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
              These Terms shall be governed by and construed in accordance with the laws of Pakistan, without regard to 
              its conflict of law provisions.
            </Typography>

            <Divider sx={{ my: 4 }} />

            <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', mb: 3 }}>
              11. Changes to Terms
            </Typography>
            <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
              We reserve the right to modify or replace these Terms at any time. If a revision is material, we will 
              provide at least 30 days' notice prior to any new terms taking effect.
            </Typography>

            <Divider sx={{ my: 4 }} />

            <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', mb: 3 }}>
              12. Contact Information
            </Typography>
            <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
              If you have any questions about these Terms of Service, please contact us at:
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
              <strong>InnoVate Games & Apps</strong><br />
              House no. 311 street 17 Villa comunity DHA Bahawalpur, Pakistan<br />
              Email: info@innovategames.net
            </Typography>
          </Paper>
        </motion.div>
      </Container>
    </Box>
  );
};

export default TermsOfService;
